import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { ToastGuidGroup } from '@Types/toastGuidGroup';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useToastGuidGroups } from '@Hooks/useToastGuidGroups';
import { useDeleteToastGuidGroup } from '@Hooks/useDeleteToastGuidGroup';
import { FolderOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ToastGuidGroupSheet from './ToastGuidGroupSheet';
import ConfirmationDialog from '@Components/ConfirmationDialog';

const TOAST_GUID_GROUP_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  description: 'filter[description]',
};

const toastGuidGroupColumnConfigs: ColumnConfig<ToastGuidGroup>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name,
    type: 'text',
    size: 250,
    cell: (info) => (
      <Link
        to={`/settings/toast-guid-groups/${info.row.original.id}`}
        className="text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {info.getValue() as string}
      </Link>
    ),
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '-',
    type: 'text',
    size: 300,
  },
  {
    id: 'toastGuidsCount',
    headerLabel: 'Toast GUIDs',
    accessor: (row) => row.guids?.length ?? 0,
    type: 'text',
    size: 120,
  },
];

export default function ToastGuidGroupList() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ToastGuidGroup | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ToastGuidGroup | null>(
    null,
  );

  const deleteMutation = useDeleteToastGuidGroup();

  const controller = useResourceTableController<ToastGuidGroup>({
    columnsConfig: toastGuidGroupColumnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    baseSearchParams: () => ({
      'filter[deletedAt]': 'null',
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: TOAST_GUID_GROUP_FIELD_MAPPING,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'description', label: 'Description' },
    ],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="sm" asChild>
          <Link
            to={`/settings/toast-guid-groups/${row.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Pencil className="h-4 w-4" />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: toastGuidGroups,
    status,
    isError,
    error,
  } = useToastGuidGroups({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Failed to load Toast GUID Groups',
      );
    }
  }, [isError, error]);

  const rows: ToastGuidGroup[] = toastGuidGroups?.data ?? [];
  const meta = toastGuidGroups?.meta;

  const handleCreate = () => {
    setSelectedGroup(null);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (group: ToastGuidGroup) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: groupToDelete.id });
      toastSuccess(`"${groupToDelete.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    } catch (err) {
      toastError(
        err instanceof Error
          ? err.message
          : 'Failed to delete Toast GUID Group',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(
      selectedGroup
        ? `"${selectedGroup.name}" has been updated.`
        : 'Toast GUID Group has been created.',
    );
  };

  return (
    <>
      <ResourceTableView<ToastGuidGroup>
        label="Toast GUID Groups"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<FolderOpen className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Toast GUID Groups: "
        renderExtraActions={() => (
          <Button variant="primary" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create Toast GUID Group
          </Button>
        )}
      />

      <ToastGuidGroupSheet
        toastGuidGroup={selectedGroup}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={groupToDelete?.name ?? ''}
        title="Delete Toast GUID Group"
        description={`Are you sure you want to delete "${groupToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the group name to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
